import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './FlikSelect.css';

/**
 * FLIK dropdown — one themed select used across the app so every dropdown looks
 * and behaves the same (a native <select>'s OS-drawn popup can't be themed).
 *
 * Two visual variants, both in the v3 language (snow-cut bevels, mono/amber):
 *  - "line": a line field for the cream form sheets (register / profile).
 *  - "box":  a bordered, beveled control for the dark app surfaces (admin).
 *
 * `options` is an array of `{ value, label }`, each optionally carrying an
 * `icon` (a React node rendered before the label, in the trigger and the list).
 * `onChange` receives the raw value (not an event). When no option matches
 * `value`, `placeholder` is shown.
 */
function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path d="m6 9 6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path d="m5 12 5 5 9-10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FlikSelect({
  value,
  onChange,
  options,
  placeholder = 'Selecciona',
  variant = 'line',
  invalid = false,
  disabled = false,
  ariaLabel,
  id,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [rect, setRect] = useState(null);
  const ref = useRef(null);
  const panelRef = useRef(null);

  const selectedIdx = options.findIndex((o) => String(o.value) === String(value));
  // Fall back to the raw value as its own label (used for locked/derived fields
  // whose value may not be present in the option list).
  const hasRawValue = value !== '' && value !== undefined && value !== null;
  const selected =
    selectedIdx >= 0
      ? options[selectedIdx]
      : hasRawValue
      ? { value, label: String(value) }
      : null;

  // Close on outside click / Escape while open.
  useEffect(() => {
    if (!open || disabled) return undefined;
    const onDocDown = (e) => {
      const inTrigger = ref.current && ref.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  // The panel is portaled to <body> so the modal's overflow/clip can't cut it
  // off. Track the trigger's viewport rect while open (and on scroll/resize) to
  // anchor the fixed-positioned panel under it.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const measure = () => {
      if (ref.current) setRect(ref.current.getBoundingClientRect());
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open]);

  // Highlight the current selection each time the panel opens.
  useEffect(() => {
    if (open) setActiveIdx(selectedIdx >= 0 ? selectedIdx : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const choose = (v) => {
    onChange(v);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    switch (e.key) {
      case 'Escape':
        setOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!open) setOpen(true);
        else setActiveIdx((i) => Math.min(options.length - 1, i + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) setOpen(true);
        else setActiveIdx((i) => Math.max(0, i - 1));
        break;
      case 'Home':
        if (open) {
          e.preventDefault();
          setActiveIdx(0);
        }
        break;
      case 'End':
        if (open) {
          e.preventDefault();
          setActiveIdx(options.length - 1);
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!open) setOpen(true);
        else if (activeIdx >= 0) choose(options[activeIdx].value);
        break;
      default:
        break;
    }
  };

  return (
    <div
      className={`FlikSelect FlikSelect--${variant}${open ? ' is-open' : ''}${
        invalid ? ' is-invalid' : ''
      }${disabled ? ' is-disabled' : ''}${className ? ` ${className}` : ''}`}
      ref={ref}
    >
      <button
        type="button"
        id={id}
        className="FlikSelect__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        aria-disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKeyDown}
      >
        <span className={`FlikSelect__value${selected ? '' : ' is-placeholder'}`}>
          {selected && selected.icon && (
            <span className="FlikSelect__optionIcon" aria-hidden="true">
              {selected.icon}
            </span>
          )}
          {selected ? selected.label : placeholder}
        </span>
        <ChevronIcon />
      </button>

      {open && rect &&
        createPortal(
          <div
            ref={panelRef}
            className={`FlikSelect FlikSelect--${variant} is-open FlikSelect--portal`}
            style={{
              position: 'fixed',
              top: rect.bottom,
              left: rect.left,
              width: rect.width,
              zIndex: 1000,
            }}
          >
            <ul className="FlikSelect__panel" role="listbox" aria-label={ariaLabel}>
          {options.map((o, i) => {
            const active = i === selectedIdx;
            const highlighted = i === activeIdx;
            return (
              <li key={o.value === '' ? '__empty' : o.value} role="option" aria-selected={active}>
                <button
                  type="button"
                  className={`FlikSelect__option${active ? ' is-active' : ''}${
                    highlighted ? ' is-highlighted' : ''
                  }`}
                  onClick={() => choose(o.value)}
                  onMouseEnter={() => setActiveIdx(i)}
                >
                  <span className="FlikSelect__optionLabel">
                    {o.icon && (
                      <span className="FlikSelect__optionIcon" aria-hidden="true">
                        {o.icon}
                      </span>
                    )}
                    {o.label}
                  </span>
                  {active && <CheckIcon />}
                </button>
              </li>
            );
          })}
            </ul>
          </div>,
          document.body
        )}
    </div>
  );
}

export default FlikSelect;
